import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { MockExamDataRegistrationStats } from "@/features/learning-content/components/MockExamDataRegistrationStats";
import { MockExamDataTable } from "@/features/learning-content/components/MockExamDataTable";
import { MockExamInfoCard } from "@/features/learning-content/components/MockExamInfoCard";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useMockExam } from "@/features/learning-content/hooks/useMockExam";
import { useMockExamQuestionList } from "@/features/learning-content/hooks/useMockExamQuestionList";
import { useSyncMockExam } from "@/features/learning-content/hooks/useSyncMockExam";
import { useUpdateMockExamMemo } from "@/features/learning-content/hooks/useUpdateMockExamMemo";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { Box, CircularProgress, Stack, Typography, styled } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Mock exam detail page - displays exam info, registration stats, and question data table
export const MockExamDetailPage: FC = () => {
  const { id, mockExamId } = useParams();
  const navigate = useNavigate();
  const { data: industryData } = useIndustryDetail(id);
  const { data: mockExamData, isLoading: isLoadingMockExam } = useMockExam(id, mockExamId);
  const questionList = useMockExamQuestionList(id, mockExamId);
  const updateMemo = useUpdateMockExamMemo(id, mockExamId);
  const syncMutation = useSyncMockExam(id, mockExamId);

  const industryName = industryData?.name ?? "";

  const handleMemoBlur = useCallback(
    (value: string) => {
      updateMemo.mutate({ internalMemo: value });
    },
    [updateMemo.mutate],
  );

  // Build info for MockExamInfoCard
  const examInfo = useMemo(
    () => ({
      name: mockExamData?.name ?? "",
      examDuration: mockExamData?.examDuration ?? null,
      totalScore: mockExamData?.totalScore ?? null,
      passingScore: mockExamData?.passingScore ?? null,
      internalMemo: mockExamData?.internalMemo ?? null,
      onMemoBlur: handleMemoBlur,
      isSavingMemo: updateMemo.isPending,
    }),
    [mockExamData, updateMemo.isPending, handleMemoBlur],
  );

  // Build stats for MockExamDataRegistrationStats
  const stats = useMemo(
    () => ({
      totalSubjectCount: mockExamData?.totalSubjectCount ?? 0,
      totalQuestionCount: questionList.totalCount,
      totalScoreSum: mockExamData?.totalScoreSum ?? 0,
      lastImportBy: mockExamData?.lastImportBy ?? "",
      lastImportAt: mockExamData?.lastImportAt ?? "",
    }),
    [mockExamData?.totalSubjectCount, mockExamData?.totalScoreSum, mockExamData?.lastImportBy, mockExamData?.lastImportAt, questionList.totalCount],
  );

  const handleBack = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  const handleEditMockExam = () => {
    if (id && mockExamId) {
      navigate(getDynamicRoute(AppRoutes.learningContentMockExamEdit, { id, mockExamId }));
    }
  };

  // Open spreadsheet in new tab
  const handleOpenSheet = () => {
    if (mockExamData?.spreadsheetUrl) {
      window.open(mockExamData.spreadsheetUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Sync handler - call real sync API
  const handleSync = () => syncMutation.mutate();

  if (isLoadingMockExam) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DetailWrapper gap={4}>
      <TopPage
        title={<Typography variant="head32Bold">{industryName}のコンテンツ管理 - 模擬試験</Typography>}
        description={
          <Typography variant="body14">
            対象分野の模擬試験コンテンツを管理・編集できます。試験内容が変わった場合は、模擬試験情報を編集してからデータを同期してください。
          </Typography>
        }
        enablePageBack={false}
      />

      <MenuNavigate
        menuPrefix={
          <Box sx={{ p: "16px 16px 12px 16px" }}>
            <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
              模擬試験
            </Typography>
          </Box>
        }
        contents={[
          {
            title: "試験情報",
            content: <MockExamInfoCard info={examInfo} />,
          },
          {
            title: "現在のデータ登録数",
            content: <MockExamDataRegistrationStats stats={stats} />,
          },
          {
            title: "模擬試験データ",
            content: (
              <MockExamDataTable
                isLoading={questionList.isLoading}
                isFetching={questionList.isFetching}
                data={questionList.data}
                totalPage={questionList.totalPage}
                totalCount={questionList.totalCount}
                page={questionList.page}
                onPageChange={questionList.onPageChange}
                onOpenSheet={handleOpenSheet}
                onSync={handleSync}
                isSyncing={syncMutation.isPending}
              />
            ),
          },
        ]}
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm-round" variant="secondary" onClick={handleBack}>
                戻る
              </Button>
              <Button variant="outline" onClick={handleEditMockExam}>
                模擬試験情報を編集
              </Button>
            </Stack>
          </FooterContainer>
        }
      />
    </DetailWrapper>
  );
};

const DetailWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
